import { useQuery } from "@tanstack/react-query";
import api, { RestApiOptions } from "@/api";
import getApplicationEnvironment from "@/utils/getApplicationEnvironment";
const { isDevEnv, isFeatureEnv } = getApplicationEnvironment();
import { ILessonPlanNode } from "@/view/ContentDisplay/TeacherTools/LessonPlannerTool/LessonPlan/utils/useLessonPlanEditor/useLessonPlanEditor";

const options: RestApiOptions = {
  basePath: isDevEnv || isFeatureEnv ? "https://app.dev.newseladev.com/" : "/",
  timeout: 40000,
};
const resource = `seti/generate-lesson-plan/?format=prosemirror`;

export interface LessonPlanResponse {
  prosemirror_document: ILessonPlanNode;
}

export interface LessonPlanRequestInterface {
  contentId: string;
  learningObjective: string;
  allottedTime: number;
  includeProjectIdea: boolean;
  includeMultilingualLearners: boolean;
  includeAssessmentQuestions: boolean;
  selectedStandards: any[];
  skip?: boolean;
}

export function useGetLessonPlan({
  contentId,
  learningObjective,
  allottedTime,
  includeProjectIdea,
  includeMultilingualLearners,
  includeAssessmentQuestions,
  selectedStandards,
  skip = false,
}: LessonPlanRequestInterface) {
  const standards = selectedStandards?.map((standard) => standard.id);
  const hasRequiredParams =
    Boolean(contentId) && Boolean(learningObjective) && standards.length > 0;

  const query = useQuery<LessonPlanResponse>({
    enabled: hasRequiredParams && !skip,
    queryKey: [
      "lesson-plan",
      contentId,
      learningObjective,
      standards,
      allottedTime,
      includeProjectIdea,
      includeMultilingualLearners,
      includeAssessmentQuestions,
    ],
    retry: 3,
    queryFn: async () =>
      api(options).post(
        resource,
        {
          article_story_content_id: contentId,
          standard_content_ids: standards,
          learning_objective: learningObjective,
          allotted_time: allottedTime,
          project_ideas: includeProjectIdea,
          support_of_multilingual_learners: includeMultilingualLearners,
          additional_assessment_questions: includeAssessmentQuestions,
        },
        {},
      ),
    refetchOnWindowFocus: false,
  });

  if (skip) {
    return {
      loading: false,
      data: null,
      error: null,
    };
  }

  const hasQueryError = !query?.isLoading && query?.isError;

  if (hasQueryError || !hasRequiredParams) {
    const errorMessage = !hasRequiredParams
      ? "A required parameter (contentId, learningObjective, selectedStandards) is missing"
      : query?.error;
    console.error("Failed to generate lesson plan:", errorMessage);

    return {
      ...query,
      loading: false,
      error: errorMessage,
      data: undefined,
    };
  }

  return { loading: query?.isFetching, ...query };
}

export default useGetLessonPlan;
